(function () {
    'use strict';

    if (typeof window === 'undefined') return;
    if (window.__AGB_ERE_LOADED__) return;
    window.__AGB_ERE_LOADED__ = true;

    // ===== Defaults (baked-in, used until /api/ere-config responds) ==========
    // MUST stay in sync with backend's ERE_DEFAULTS (defaults.py) and the
    // panel's ERE_DEFAULTS (defaults.ts). When you change one, change all three.
    const FALLBACK_CONFIG = {
        schemaVersion: 1,
        enabled: true,
        sampleRate: 1.0,
        autoCaptureGlobalErrors: true,
        autoCaptureConsoleError: false,
        severityFilter: 'all',
        retentionDays: 14,
        throttleWindowMinutes: 15,
        alerts: {
            emailEnabled: false,
            emailRecipients: [],
            webhookEnabled: false,
            webhookUrl: '',
            minSeverity: 'critical',
            minOccurrences: 1
        },
        ignorePatterns: [
            'ResizeObserver loop',
            '^Script error\\.?$',
            'Non-Error promise rejection captured'
        ],
        // Policy: critical == purchase-blocking ONLY.
        //   - cart-quantity-cap: JS exceptions inside the cap-enforcement
        //     script itself (a bug here can block legit cart adds). The
        //     script doesn't set its own severity, so the bumper here
        //     promotes any error from that source to critical.
        //
        // NOTE: shopify-purchase-watchdog is INTENTIONALLY NOT in this list
        // anymore. The watchdog assigns its OWN severity per URL pattern:
        //   - /cart/add.js + wallet failures -> critical (sale-blocking)
        //   - /cart/(change|update|clear).js -> warn (annoying not blocking;
        //     these are common on flaky mobile during Drip blasts and were
        //     producing 100+ critical alerts/hour of pure noise).
        // Including watchdog here re-promoted those warn-level reports back
        // to critical, defeating the watchdog's nuance. Trust the watchdog.
        // Everything else stays at 'warn' and is triaged from the dashboard.
        // MUST stay in sync with backend + panel defaults.
        criticalPaths: [
            'cart-quantity-cap'
        ],
        // cart-MODIFY network failures (/cart/change|update|clear) are
        // annoyance-level, never purchase-blocking, and spike on flaky mobile
        // during email blasts. The watchdog already tags them 'warn'; pinning
        // them here guarantees no criticalPaths bump can ever re-promote a
        // "/cart/update.js unreachable (network failure)" report to critical.
        nonCriticalPatterns: [
            '/cart/(change|update|clear)(\\.js)? unreachable \\(network failure\\)'
        ]
    };

    const CONFIG_TTL_MS = 5 * 60 * 1000;
    // Hard rate limit so a runaway exception loop can't flood the backend.
    // Counts events DISPATCHED (post-filter); ignored events don't burn budget.
    const MAX_EVENTS_PER_MINUTE = 60;

    // ===== Env detection (mirrors cart-quantity-cap.js) =======================
    function detectStorefrontEnv() {
        try {
            const urlForce = new URLSearchParams(location.search).get('storefront_env');
            if (urlForce === 'preview' || urlForce === 'live') return urlForce;
        } catch (_) {}
        try {
            const stored = localStorage.getItem('AGB_StorefrontEnv');
            if (stored === 'preview' || stored === 'live') return stored;
        } catch (_) {}
        const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
        if (role === 'main') return 'live';
        if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
        const host = (location && location.hostname) || '';
        if (host.endsWith('.shopifypreview.com')) return 'preview';
        if (host.endsWith('.myshopify.com')) return 'preview';
        if (host === '127.0.0.1' || host === 'localhost') return 'preview';
        try {
            if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
        } catch (_) {}
        return 'preview';
    }

    const ENV = detectStorefrontEnv();
    const LS_CONFIG_KEY = `AGB_EREConfig_${ENV}`;
    const LS_CONFIG_ETAG = `AGB_EREConfigEtag_${ENV}`;
    const LS_CONFIG_AT = `AGB_EREConfigFetchedAt_${ENV}`;

    const state = {
        config: FALLBACK_CONFIG,
        configLoaded: false,
        // Pre-config buffer. Errors that fire before /api/ere-config returns
        // land here; on config load we flush them through the filter pipeline.
        // Capped at 50 so a boot-time error storm can't grow this unbounded.
        buffer: [],
        seen: new Set(),
        // Sliding-window rate counter. Holds timestamps of the last
        // MAX_EVENTS_PER_MINUTE events; any older entry is evicted before
        // the check. Events past the limit get dropped silently.
        recent: [],
        // Latest dispatched event for window.ERE.state diagnostics.
        lastDispatched: null,
        // Counts for diagnostics.
        counts: { captured: 0, dispatched: 0, dropped: 0, ignored: 0 }
    };

    // Compiled regex caches. Re-built when config changes.
    let ignoreRegexes = [];
    let nonCriticalRegexes = [];

    function compilePatterns(patterns) {
        const out = [];
        for (const p of (patterns || [])) {
            if (typeof p !== 'string' || !p) continue;
            try {
                out.push(new RegExp(p));
            } catch (_) {
                // Invalid pattern -- panel-side validation catches it on save
                // but a third party may have shipped a bad regex via override;
                // skip silently here.
            }
        }
        return out;
    }

    function tryParseConfig(raw) {
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.config) return parsed.config;
        } catch (_) {}
        return null;
    }

    function applyConfig(next) {
        state.config = Object.assign({}, FALLBACK_CONFIG, next || {});
        // Defensive: make sure nested objects exist even if the server
        // returned a partial.
        state.config.alerts = Object.assign({}, FALLBACK_CONFIG.alerts, next && next.alerts);
        ignoreRegexes = compilePatterns(state.config.ignorePatterns);
        nonCriticalRegexes = compilePatterns(state.config.nonCriticalPatterns);
    }

    // Cached config (use immediately while we wait for the network).
    try {
        const cached = tryParseConfig(localStorage.getItem(LS_CONFIG_KEY));
        if (cached) applyConfig(cached);
    } catch (_) {}

    // ===== Config fetch =======================================================
    async function fetchConfig(force) {
        const base = window.AGB_ERE_CONFIG_URL
            || 'http://localhost:8000/api/ere-config';
        const endpoint = ENV === 'preview'
            ? `${base}${base.indexOf('?') === -1 ? '?' : '&'}env=preview`
            : base;
        try {
            if (!force) {
                const lastFetched = parseInt(localStorage.getItem(LS_CONFIG_AT) || '0', 10);
                if (Date.now() - lastFetched < CONFIG_TTL_MS) {
                    state.configLoaded = true;
                    flushBuffer();
                    return;
                }
            }
            const etag = localStorage.getItem(LS_CONFIG_ETAG);
            const headers = { 'Accept': 'application/json' };
            if (etag && !force) headers['If-None-Match'] = etag;
            const res = await fetch(endpoint, { headers });
            localStorage.setItem(LS_CONFIG_AT, String(Date.now()));
            if (res.status === 304) {
                state.configLoaded = true;
                flushBuffer();
                return;
            }
            if (!res.ok) {
                state.configLoaded = true;
                flushBuffer();
                return;
            }
            const newEtag = res.headers.get('ETag');
            if (newEtag) localStorage.setItem(LS_CONFIG_ETAG, newEtag);
            const body = await res.json();
            if (body && body.config) {
                applyConfig(body.config);
                try { localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(body)); } catch (_) {}
            }
        } catch (_) {
            // Network error: keep FALLBACK_CONFIG. NEVER let a failed
            // config fetch propagate -- the reporter is supposed to be
            // the most resilient script on the page.
        } finally {
            state.configLoaded = true;
            flushBuffer();
        }
    }

    function normalize(s) {
        if (typeof s !== 'string') return '';
        let out = s.trim();
        // Strip long numbers (often IDs / timestamps that fragment the FP).
        out = out.replace(/\b\d{6,}\b/g, '<num>');
        // Strip URLs.
        out = out.replace(/https?:\/\/\S+/g, '<url>');
        out = out.replace(/\s+/g, ' ');
        return out;
    }

    function fingerprintFor(message, source) {
        const key = normalize(message) + '::' + (source || '');
        let h = 5381;
        for (let i = 0; i < key.length; i++) {
            h = ((h << 5) + h + key.charCodeAt(i)) | 0;
        }
        return (h >>> 0).toString(16).padStart(8, '0');
    }

    function matchesAnyIgnorePattern(message, source) {
        for (const rx of ignoreRegexes) {
            try {
                if (rx.test(message) || rx.test(source)) return true;
            } catch (_) {}
        }
        return false;
    }

    function severityPassesFilter(severity) {
        const filter = state.config.severityFilter || 'all';
        if (filter === 'all') return true;
        const rank = { info: 0, warn: 1, critical: 2 };
        const sev = rank[severity] != null ? rank[severity] : 1;
        if (filter === 'warn+') return sev >= 1;
        if (filter === 'critical') return sev >= 2;
        return true;
    }

    function matchesAnyNonCritical(message) {
        for (const rx of nonCriticalRegexes) {
            try {
                if (rx.test(message)) return true;
            } catch (_) {}
        }
        return false;
    }

    function bumpCriticalForPath(severity, source, message) {
        if (severity === 'critical') return severity;
        // HARDCODED carve-out (config-independent): cart-MODIFY failures
        // (/cart/change|update|clear) from the purchase-watchdog are annoyance-
        // level, never purchase-blocking. No criticalPaths config — live,
        // stored, or stale — can promote them to critical. cart-ADD + wallet
        // are NOT matched (they stay critical). The backend ERE store enforces
        // the same carve-out, so this is belt-and-suspenders.
        try {
            if (String(source || '').toLowerCase().indexOf('purchase-watchdog') !== -1 &&
                /\/cart\/(change|update|clear)(\.js)?/i.test(String(message || ''))) {
                return severity;
            }
        } catch (_e) {}
        const paths = state.config.criticalPaths || [];
        const src = (source || '').toLowerCase();
        let inCriticalPath = false;
        for (const p of paths) {
            if (p && src.indexOf(String(p).toLowerCase()) >= 0) { inCriticalPath = true; break; }
        }
        if (!inCriticalPath) return severity;
        // Source matches a critical path -- but suppress the bump when the
        // message is in the known-non-blocking carve-out list.
        if (matchesAnyNonCritical(message)) return severity;
        return 'critical';
    }

    function shouldSample(severity) {
        if (severity === 'critical') return true;
        const r = Number(state.config.sampleRate);
        if (!isFinite(r) || r >= 1) return true;
        if (r <= 0) return false;
        return Math.random() < r;
    }

    function withinRateLimit() {
        const now = Date.now();
        // Drop entries older than 60s.
        while (state.recent.length && (now - state.recent[0]) > 60_000) {
            state.recent.shift();
        }
        if (state.recent.length >= MAX_EVENTS_PER_MINUTE) return false;
        state.recent.push(now);
        return true;
    }

    // ===== Dispatch ==========================================================
    function buildPayload(input) {
        const message = String(input.message || '');
        const source = String(input.source || '');
        const stack = String(input.stack || '').slice(0, 8000);
        const url = String(input.url || (location && location.href) || '').slice(0, 2000);
        const userAgent = String((navigator && navigator.userAgent) || '').slice(0, 500);
        const context = (input.context && typeof input.context === 'object') ? input.context : {};
        return {
            message: message.slice(0, 2000),
            severity: input.severity || 'warn',
            source: source.slice(0, 500),
            stack,
            url,
            userAgent,
            theme: ENV,
            context,
            reportedAt: new Date().toISOString(),
            errorKey: '' // server computes a dedup hash when empty
        };
    }

    function dispatch(payload) {
        // SAFETY GATE (session-guard.js). Error reports carry the session's
        // identity + page context, so one raised while browsing as someone else
        // would be filed against them. Team/test sessions may still report (the
        // marker below tags the row so it can be excluded); an assumed session
        // may not. Fails OPEN only when the guard is absent, because losing
        // genuine shopper error reports would be worse than a tagged extra row.
        // NOTE: deliberately does NOT attach session_kind. The panel's
        // ErrorReportPayload model is `extra="forbid"`, so an unknown key makes
        // the whole report 400 — and because sendBeacon discards the response
        // and the fetch fallback swallows errors, that failure is SILENT. It
        // would have stopped ERE receiving anything from staff/test browsers,
        // i.e. exactly the sessions the Fake Customer Engine drives through live
        // to find purchase-blocking bugs. Add the field to the panel model FIRST
        // (and deploy panel-back BEFORE the theme) if this is ever wanted.
        try {
            if (window.SE_Session && !window.SE_Session.allow('errors', 'error report')) return;
        } catch (_) { /* guard unavailable — proceed as a normal shopper */ }
        const base = window.AGB_ERE_EVENT_URL
            || (window.AGB_ERE_CONFIG_URL
                ? window.AGB_ERE_CONFIG_URL.replace(/\/api\/ere-config.*$/, '/api/ere/event')
                : 'http://localhost:8000/api/ere/event');
        const body = JSON.stringify(payload);
        // text/plain blob avoids the CORS preflight on POST. Backend reads
        // the body as raw bytes and parses JSON regardless of Content-Type.
        let beaconOk = false;
        if (navigator && typeof navigator.sendBeacon === 'function') {
            try {
                beaconOk = navigator.sendBeacon(base, new Blob([body], { type: 'text/plain' }));
            } catch (_) { beaconOk = false; }
        }
        if (!beaconOk) {
            // keepalive lets the request outlive the page (e.g. on unload).
            try {
                fetch(base, {
                    method: 'POST',
                    headers: { 'Content-Type': 'text/plain' },
                    body,
                    keepalive: true,
                    mode: 'cors'
                }).catch(function () {});
            } catch (_) {}
        }
        state.lastDispatched = payload;
        state.counts.dispatched++;
    }

    function capture(input) {
        state.counts.captured++;
        if (!state.configLoaded) {
            // Buffer until config arrives.
            if (state.buffer.length < 50) state.buffer.push(input);
            return;
        }
        if (!state.config.enabled) { state.counts.dropped++; return; }
        const message = String(input.message || '');
        const source = String(input.source || '');
        if (matchesAnyIgnorePattern(message, source)) {
            state.counts.ignored++;
            return;
        }
        let severity = bumpCriticalForPath(input.severity || 'warn', source, message);
        if (!severityPassesFilter(severity)) { state.counts.dropped++; return; }
        if (!shouldSample(severity)) { state.counts.dropped++; return; }
        // Per-page dedup. Cheap protection -- if a script has a tight loop
        // throwing the same error, we only send it once per page load.
        const fp = fingerprintFor(message, source);
        if (state.seen.has(fp)) { state.counts.dropped++; return; }
        state.seen.add(fp);
        if (!withinRateLimit()) { state.counts.dropped++; return; }
        const payload = buildPayload({ ...input, severity });
        dispatch(payload);
    }

    function flushBuffer() {
        if (!state.buffer.length) return;
        const items = state.buffer.splice(0, state.buffer.length);
        for (const it of items) capture(it);
    }

    // ===== Public API ========================================================
    function report(errOrMessage, ctx) {
        try {
            const isErr = errOrMessage && typeof errOrMessage === 'object' && 'message' in errOrMessage;
            const message = isErr ? String(errOrMessage.message) : String(errOrMessage);
            const stack = isErr && errOrMessage.stack ? String(errOrMessage.stack) : '';
            const source = (ctx && ctx.source)
                || (isErr && errOrMessage.fileName)
                || extractSourceFromStack(stack)
                || '';
            capture({
                message,
                severity: (ctx && ctx.severity) || 'warn',
                source,
                stack,
                url: ctx && ctx.url,
                context: ctx && ctx.context
            });
        } catch (_) {
            // report() must never throw -- swallowing here is the right call.
        }
    }

    function reportCritical(errOrMessage, ctx) {
        report(errOrMessage, Object.assign({}, ctx || {}, { severity: 'critical' }));
    }

    function extractSourceFromStack(stack) {
        if (!stack) return '';
        // First non-anonymous frame typically has `at fn (URL:line:col)`.
        const m = stack.match(/(https?:\/\/[^\s)]+|[\w./-]+\.js):(\d+)(?::(\d+))?/);
        return m ? m[0] : '';
    }

    // ===== Global handlers ===================================================
    // Installed IMMEDIATELY (before config fetch) so we don't miss boot-time
    // errors. The autoCaptureGlobalErrors gate is enforced inside the
    // handlers (skip when off) rather than by not installing them, so a
    // panel toggle takes effect within the 5-minute window without a reload.
    function onErrorHandler(ev) {
        if (state.configLoaded && !state.config.autoCaptureGlobalErrors) return;
        const message = (ev && ev.message) || 'Uncaught error';
        const source = (ev && ev.filename) || '';
        const stack = (ev && ev.error && ev.error.stack) || '';
        capture({ message: String(message), severity: 'warn', source, stack });
    }

    function onUnhandledRejection(ev) {
        if (state.configLoaded && !state.config.autoCaptureGlobalErrors) return;
        const reason = ev && ev.reason;
        let message;
        let stack = '';
        let source = '';
        if (reason && typeof reason === 'object') {
            message = reason.message || String(reason);
            stack = reason.stack || '';
            source = extractSourceFromStack(stack);
        } else {
            message = String(reason);
        }
        capture({ message: 'Unhandled rejection: ' + message, severity: 'warn', source, stack });
    }

    window.addEventListener('error', onErrorHandler, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    // ===== Shopify purchase watchdog ==========================================
    //
    // Wraps fetch + XHR to catch HTTP failures on purchase-critical Shopify
    // endpoints REGARDLESS of which script issued the request. This complements
    // (and goes beyond) the per-script reporting inside cart-quantity-cap.js:
    // an add-to-cart fired from theme.js, from a custom buy-buttons element,
    // from the gear builder, or from anywhere else also gets reported here.
    //
    // Severity policy: split by endpoint class so the alert inbox stays
    // signal-rich. AbortError network failures (user closed tab / nav-back /
    // BFCache resume) are suppressed entirely -- they're cancelled by the
    // user, not real failures.
    //
    //   /cart/add(.js)                                     -> CRITICAL
    //     The shopper couldn't add a new item to the cart.
    //     - network failure (DNS/timeout/non-Abort)        -> CRITICAL
    //     - HTTP 5xx                                       -> CRITICAL
    //     - HTTP 4xx other than 422                        -> CRITICAL
    //         (422 = Shopify's normal "out of stock / cap rejected" -- silent;
    //          400 / 403 / 404 / 429 = unexpected purchase blockers)
    //
    //   /cart/(change|update|clear)(.js)                   -> WARN
    //     The shopper's cart already exists; an adjustment didn't apply.
    //     They can retry, or proceed to checkout with whatever's currently
    //     in the cart. Annoying, not purchase-blocking.
    //     - network failure (DNS/timeout/non-Abort)        -> WARN
    //     - HTTP 5xx                                       -> WARN
    //     - HTTP 4xx other than 422                        -> SILENT
    //         (modify endpoints don't have a "real" 4xx purchase blocker;
    //          422 = normal cap reject. Other 4xx is rare and recoverable.)
    //
    //   /wallet/* (Shop Pay one-tap checkout)              -> CRITICAL
    //     - network failure or 5xx                         -> CRITICAL
    //
    // Anything that merely degrades UX without stopping a sale (cart.js GET
    // hiccup the drawer recovers from, /products/<handle>.js stale data)
    // is intentionally NOT reported here -- adding them would spam the
    // inbox because the backend's criticalPaths bump might still promote
    // them on some paths. We watch only purchase-path endpoints.
    //
    // Idempotency guard window.__AGB_PURCHASE_WATCHDOG__ so a stray double-
    // load (e.g. theme.liquid duplicates the script tag) doesn't double-wrap
    // window.fetch.
    if (!window.__AGB_PURCHASE_WATCHDOG__) {
        window.__AGB_PURCHASE_WATCHDOG__ = true;

        // Split cart endpoints into TWO severity tiers:
        //   - cart-add  -> CRITICAL. Failure here blocks the shopper from
        //                  adding a new item -- a real purchase block.
        //   - cart-modify -> WARN. /cart/change.js (cart-drawer +/-),
        //                  /cart/update.js (cart-page batch), /cart/clear.js
        //                  all touch an EXISTING cart line. A failure means
        //                  the quantity adjustment didn't apply; the shopper
        //                  retries or proceeds to checkout with whatever's
        //                  currently in the cart. Annoying, not blocking.
        // The earlier policy treated all four as critical and that produced
        // 100+ critical alerts/hour during Drip blasts on flaky mobile
        // connections -- pure noise from a backend-actionability standpoint.
        const CART_ADD_RX = /\/cart\/add(\.js)?(\?|$)/;
        const CART_MODIFY_RX = /\/cart\/(change|update|clear)(\.js)?(\?|$)/;
        const WALLET_RX = /\/wallet\//;

        function _wdClassify(url) {
            if (!url) return null;
            if (CART_ADD_RX.test(url)) return 'cart-add';
            if (CART_MODIFY_RX.test(url)) return 'cart-modify';
            if (WALLET_RX.test(url)) return 'wallet';
            return null;
        }

        // Severity per endpoint class. Used both as the storefront-side
        // severity AND as a hint for which network failures even bother
        // reporting at all -- AbortError on a modify endpoint is so noisy
        // (browser back-button, tab close, fast nav) that we drop it
        // entirely instead of cluttering the panel with warn-level events
        // that have no actionable signal.
        const _WD_SEVERITY = {
            'cart-add': 'critical',
            'cart-modify': 'warn',
            'wallet': 'critical',
        };

        function _wdReportFailure(kind, url, status, statusText, netErr) {
            let endpointLabel = url;
            try {
                const u = new URL(url, location.origin);
                endpointLabel = u.pathname + (u.search || '');
            } catch (_) { /* keep raw url */ }

            const networkFail = !status;
            const is5xx = status >= 500 && status < 600;
            const is4xxOther = status >= 400 && status < 500 && status !== 422;
            const shouldReport = networkFail || is5xx || (kind === 'cart-add' && is4xxOther);
            if (!shouldReport) return;

            // Suppress AbortError entirely -- the request was cancelled
            // because the shopper navigated away / closed the tab / clicked
            // back during BFCache resume. Not a real failure; the shopper
            // can't be helped from the backend either way. AbortError shows
            // up as either DOMException(name='AbortError') or a native Error
            // depending on browser; check both shapes.
            if (networkFail && netErr) {
                const name = (netErr && netErr.name) || '';
                if (name === 'AbortError') return;
            }

            const severity = _WD_SEVERITY[kind] || 'warn';

            const reason = networkFail
                ? ('Shopify ' + endpointLabel + ' unreachable (network failure)')
                : ('Shopify ' + endpointLabel + ' returned ' + status + (statusText ? ' ' + statusText : ''));

            try {
                report(reason, {
                    source: 'shopify-purchase-watchdog',
                    severity: severity,
                    stack: networkFail
                        ? (netErr && netErr.stack ? netErr.stack : String(netErr || ''))
                        : ('HTTP ' + status + ' ' + (statusText || '') + ' on ' + endpointLabel)
                });
            } catch (_e) {
                // ERE plumbing must never break a cart request -- swallow.
            }
        }

        // Fetch wrap. Idempotent inside the watchdog (other scripts may wrap
        // window.fetch too; this wrap delegates to whatever window.fetch is
        // at install time so it composes cleanly).
        try {
            const originalFetch = window.fetch && window.fetch.bind(window);
            if (originalFetch) {
                const wrappedFetch = async function (input, init) {
                    const url = typeof input === 'string'
                        ? input
                        : (input && input.url) || '';
                    const kind = _wdClassify(url);
                    if (!kind) {
                        return originalFetch(input, init);
                    }
                    let response;
                    try {
                        response = await originalFetch(input, init);
                    } catch (netErr) {
                        _wdReportFailure(kind, url, 0, '', netErr);
                        throw netErr;
                    }
                    try {
                        _wdReportFailure(kind, url, response.status, response.statusText, null);
                    } catch (_e) { /* swallow */ }
                    return response;
                };
                wrappedFetch.__purchaseWatchdog = true;
                window.fetch = wrappedFetch;
            }
        } catch (_e) { /* defensive */ }

        // XHR wrap for legacy callers (jQuery.ajax still uses XHR under the
        // hood; same purchase-critical traffic must be observed).
        try {
            const proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
            if (proto && !proto.__purchaseWatchdog) {
                const origOpen = proto.open;
                const origSend = proto.send;
                proto.open = function (method, url) {
                    this.__pwUrl = url || '';
                    this.__pwKind = _wdClassify(url || '');
                    return origOpen.apply(this, arguments);
                };
                proto.send = function () {
                    const xhr = this;
                    if (xhr.__pwKind) {
                        const onErr = function () {
                            _wdReportFailure(xhr.__pwKind, xhr.__pwUrl, 0, 'network error', null);
                        };
                        const onDone = function () {
                            if (xhr.readyState === 4) {
                                _wdReportFailure(xhr.__pwKind, xhr.__pwUrl, xhr.status, xhr.statusText, null);
                            }
                        };
                        xhr.addEventListener('error', onErr);
                        xhr.addEventListener('timeout', onErr);
                        xhr.addEventListener('abort', onErr);
                        xhr.addEventListener('loadend', onDone);
                    }
                    return origSend.apply(xhr, arguments);
                };
                proto.__purchaseWatchdog = true;
            }
        } catch (_e) { /* defensive */ }
    }

    // Optional console.error wrap. Installed lazily once config arrives so
    // we don't capture pre-config console.error noise (often Shopify's own
    // boot warnings). Off by default in FALLBACK_CONFIG.
    let consoleWrapped = false;
    function maybeWrapConsoleError() {
        if (consoleWrapped) return;
        if (!state.config.autoCaptureConsoleError) return;
        const original = console.error;
        console.error = function () {
            try {
                const parts = [];
                for (let i = 0; i < arguments.length; i++) {
                    const a = arguments[i];
                    parts.push(a && a.message ? a.message : String(a));
                }
                capture({
                    message: parts.join(' ').slice(0, 2000),
                    severity: 'warn',
                    source: 'console.error',
                    stack: (arguments[0] && arguments[0].stack) || ''
                });
            } catch (_) {}
            return original.apply(this, arguments);
        };
        consoleWrapped = true;
    }

    // ===== Boot ===============================================================
    window.ERE = {
        get state() { return state; },
        getConfig: function () { return state.config; },
        refetchConfig: function () {
            localStorage.removeItem(LS_CONFIG_AT);
            return fetchConfig(true).then(maybeWrapConsoleError);
        },
        report,
        reportCritical,
        flushBuffer
    };

    fetchConfig(false).then(maybeWrapConsoleError);
})();
